import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { InputBase } from '@material-ui/core';
import { Close, Search } from '@material-ui/icons';
import clsx from 'clsx';

/**
 * Shared look for list pages: a page header, dark glass panels, tables, search and tabs.
 * Matches the Dungeons & Raids pages. Electron 8 (Chromium 80) has no flexbox gap: spacing is grid gap or margins.
 */
export const PAGE_KIT = {
  accent: '#42a5f5',
  muted: 'rgba(255, 255, 255, 0.6)',
  faint: 'rgba(255, 255, 255, 0.4)',
  divider: '1px solid rgba(255, 255, 255, 0.07)',
  panel: 'linear-gradient(180deg, rgba(26, 27, 32, 0.95), rgba(18, 18, 22, 0.95))',
  border: '1px solid rgba(255, 255, 255, 0.09)',
  shadow: '0 12px 32px rgba(0, 0, 0, 0.28)',
  /** buttons and inputs in a header share this height so they line up */
  controlHeight: 40,
};

export const usePageStyles = makeStyles((theme) => ({
  panel: {
    position: 'relative',
    background: PAGE_KIT.panel,
    border: PAGE_KIT.border,
    borderRadius: 14,
    boxShadow: PAGE_KIT.shadow,
    padding: theme.spacing(2, 2.25),
  },
  /** a panel holding a table edge to edge */
  tablePanel: {
    position: 'relative',
    background: PAGE_KIT.panel,
    border: PAGE_KIT.border,
    borderRadius: 14,
    boxShadow: PAGE_KIT.shadow,
    overflow: 'hidden',
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
    '& .MuiTableCell-head': {
      fontSize: '0.7rem',
      fontWeight: 800,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: PAGE_KIT.muted,
      background: 'rgba(255, 255, 255, 0.03)',
    },
    '& .ReactVirtualized__Table__headerRow': {
      background: 'rgba(255, 255, 255, 0.03)',
      borderBottom: PAGE_KIT.divider,
      textTransform: 'uppercase',
      '& .MuiTableCell-head': {
        fontSize: '0.7rem',
        fontWeight: 800,
        letterSpacing: '0.1em',
        color: PAGE_KIT.muted,
      },
    },
    '& .ReactVirtualized__Table__row': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
      transition: 'background 0.12s',
      '&:hover': {
        background: 'rgba(66, 165, 245, 0.05)',
      },
    },
  },
  headCell: {
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
    background: 'rgba(255, 255, 255, 0.03)',
    whiteSpace: 'nowrap',
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.05)',
    },
  },
  sectionTitle: {
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
    marginBottom: theme.spacing(0.75),
  },
  subtitle: {
    color: PAGE_KIT.muted,
    fontSize: '0.9em',
  },
  muted: {
    color: PAGE_KIT.muted,
    fontSize: '0.85em',
  },
  strong: {
    fontWeight: 700,
  },
  link: {
    color: '#fff',
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'color 0.12s',
    '&:hover': {
      color: '#90caf9',
    },
  },
  empty: {
    display: 'grid',
    justifyItems: 'center',
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: PAGE_KIT.muted,
  },
  /** full-width "load more" bar at the bottom of a table panel */
  loadMore: {
    width: '100%',
    height: 42,
    borderRadius: 0,
    textTransform: 'none',
    fontWeight: 700,
    color: '#90caf9',
    borderTop: PAGE_KIT.divider,
    background: 'rgba(255, 255, 255, 0.02)',
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.08)',
    },
  },
  /** a row of filter controls (grid, since flex gap doesn't work in Electron 8) */
  filters: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    overflowX: 'auto',
    // room above for outlined inputs' floating labels, which a scrolling row would otherwise clip
    paddingTop: 8,
    paddingBottom: 2,
  },
  /** a primary action in a header, the same height as PageSearch */
  primaryButton: {
    height: PAGE_KIT.controlHeight,
    padding: '0 18px',
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 800,
    fontSize: '0.92rem',
    whiteSpace: 'nowrap',
    color: '#fff',
    background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
    boxShadow: '0 6px 16px rgba(30, 136, 229, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    transition: 'filter 0.15s, box-shadow 0.15s',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
      filter: 'brightness(1.12)',
      boxShadow: '0 8px 20px rgba(30, 136, 229, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.6)',
      background: 'rgba(66, 165, 245, 0.25)',
      boxShadow: 'none',
    },
  },
  /** a quieter action next to the primary one */
  secondaryButton: {
    height: PAGE_KIT.controlHeight,
    padding: '0 16px',
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.85)',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.12)',
    },
  },
  /** a small rounded tag */
  pill: {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 9,
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.85)',
    background: 'rgba(255, 255, 255, 0.07)',
  },

  // PageHeader
  header: {
    marginBottom: theme.spacing(2),
    borderRadius: 14,
    overflow: 'hidden',
    background: 'linear-gradient(180deg, rgba(28, 29, 35, 0.97), rgba(18, 18, 22, 0.97))',
    border: PAGE_KIT.border,
    boxShadow: '0 14px 36px rgba(0, 0, 0, 0.3)',
  },
  headerTop: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2.25, 2.75, 2),
    background: 'radial-gradient(ellipse at top left, rgba(66, 165, 245, 0.14), transparent 55%)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  headerWithIcon: {
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'auto minmax(0, 1fr)',
    },
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 13,
    display: 'grid',
    placeItems: 'center',
    color: PAGE_KIT.accent,
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.32)',
    '& svg': {
      fontSize: 26,
    },
  },
  headerTitle: {
    fontSize: '1.5rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  headerSubtitle: {
    marginTop: 3,
    fontSize: '0.9rem',
    color: PAGE_KIT.muted,
  },
  headerActions: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
      justifyContent: 'start',
    },
  },
  headerBody: {
    padding: theme.spacing(0, 2.75, 2),
  },
  /** tabs or filters straight under the title: a darker strip across the bottom of the header */
  headerStrip: {
    padding: theme.spacing(0, 2),
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(0, 0, 0, 0.15)',
    // MUI Tabs inside the strip
    '& .MuiTabs-root': {
      minHeight: 46,
      borderBottom: 0,
    },
    '& .MuiTab-root': {
      minHeight: 46,
      minWidth: 0,
      padding: theme.spacing(0, 1.75),
      textTransform: 'none',
      fontSize: '0.92rem',
      fontWeight: 700,
      color: 'rgba(255, 255, 255, 0.6)',
      opacity: 1,
      '&:hover': {
        color: '#fff',
      },
    },
    '& .MuiTab-root.Mui-selected': {
      color: '#fff',
    },
    '& .MuiTabs-indicator': {
      height: 3,
      borderRadius: '3px 3px 0 0',
      background: PAGE_KIT.accent,
      boxShadow: `0 0 10px ${PAGE_KIT.accent}`,
    },
  },
  headerFilters: {
    padding: theme.spacing(0.75, 2.75, 1.5),
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    background: 'rgba(0, 0, 0, 0.15)',
  },

  // PageSearch
  search: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    width: 300,
    maxWidth: '100%',
    height: PAGE_KIT.controlHeight,
    boxSizing: 'border-box',
    padding: '0 6px 0 12px',
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: PAGE_KIT.accent,
      boxShadow: '0 0 0 3px rgba(66, 165, 245, 0.15)',
    },
  },
  searchIcon: {
    fontSize: 20,
    color: PAGE_KIT.muted,
    marginRight: 8,
  },
  searchInput: {
    fontSize: '0.92rem',
    '& input': {
      padding: 0,
      height: PAGE_KIT.controlHeight - 2,
    },
  },
  searchClear: {
    width: 26,
    height: 26,
    borderRadius: 7,
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    color: PAGE_KIT.muted,
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.1)',
    },
    '& svg': {
      fontSize: 16,
    },
  },

  // PageEmpty
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    display: 'grid',
    placeItems: 'center',
    marginBottom: theme.spacing(2),
    color: PAGE_KIT.accent,
    background: 'rgba(66, 165, 245, 0.12)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
    boxShadow: '0 0 30px rgba(66, 165, 245, 0.15)',
    '& svg': {
      fontSize: 32,
    },
  },
  emptyTitle: {
    fontSize: '1.1rem',
    fontWeight: 800,
    color: '#fff',
  },
  emptyText: {
    marginTop: 4,
    maxWidth: 440,
    fontSize: '0.9rem',
    color: PAGE_KIT.muted,
  },
  emptyAction: {
    marginTop: theme.spacing(2.5),
  },
}));

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** an icon in a tile left of the title */
  icon?: React.ReactNode;
  /** buttons, search, etc. on the right; give them PAGE_KIT.controlHeight (or PageSearch / primaryButton) so they line up */
  actions?: React.ReactNode;
  /**
   * under the title row. 'strip' (the default) puts it in a darker band across the bottom of the header, which suits tabs;
   * 'filters' does the same with padding for filter controls; 'plain' just pads it.
   */
  children?: React.ReactNode;
  childrenStyle?: 'strip' | 'filters' | 'plain';
}

/** A page's title panel: title and subtitle on the left, actions on the right, tabs or filters underneath. */
export const PageHeader: React.FC<PageHeaderProps> = ({title, subtitle, icon, actions, children, childrenStyle = 'strip'}) => {
  const classes = usePageStyles();
  return (
    <div className={classes.header}>
      <div className={clsx(classes.headerTop, {[classes.headerWithIcon]: icon != null})}>
        {icon && <div className={classes.headerIcon}>{icon}</div>}
        <div style={{minWidth: 0}}>
          <div className={classes.headerTitle}>{title}</div>
          {subtitle && <div className={classes.headerSubtitle}>{subtitle}</div>}
        </div>
        {actions ? <div className={classes.headerActions}>{actions}</div> : <span />}
      </div>
      {children &&
        <div className={childrenStyle == 'plain' ? classes.headerBody : childrenStyle == 'filters' ? classes.headerFilters : classes.headerStrip}>
          {children}
        </div>
      }
    </div>
  );
}

interface PageSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: number;
}

/** The header search box: the same height as primaryButton, with a clear button. */
export const PageSearch: React.FC<PageSearchProps> = ({value, onChange, placeholder = 'Search', width}) => {
  const classes = usePageStyles();
  return (
    <div className={classes.search} style={width ? {width} : undefined}>
      <Search className={classes.searchIcon} />
      <InputBase className={classes.searchInput} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
        inputProps={{'aria-label': placeholder}} />
      {value ? <span className={classes.searchClear} title="Clear" onClick={() => onChange('')}><Close /></span> : <span />}
    </div>
  );
}

interface PageEmptyProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  text?: React.ReactNode;
  action?: React.ReactNode;
}

/** An empty list: an icon tile, a headline, a line of help and an optional action. */
export const PageEmpty: React.FC<PageEmptyProps> = ({icon, title, text, action}) => {
  const classes = usePageStyles();
  return (
    <div className={classes.empty}>
      {icon && <div className={classes.emptyIcon}>{icon}</div>}
      <div className={classes.emptyTitle}>{title}</div>
      {text && <div className={classes.emptyText}>{text}</div>}
      {action && <div className={classes.emptyAction}>{action}</div>}
    </div>
  );
}
